import type { KeyboardEvent } from 'react'

import { useEffect, useState } from 'react'

import type { ChatInputProps } from './chat.types'
import type { ChatInputStatus } from './chat.types'

import Box from '../box'
import Button from '../button'
import { styled, useStyleConfig, vui } from '../core'
import Icon from '../icon'
import { inputColors } from '../input/consts'
import HelpText from '../input/helpText'
import { cs } from '../utils'
import ChatInputButton from './chatInputButton'

const CustomTextarea = styled.textareaBox`
  border-radius: none;
  border-width: 0;
  outline: none;
  padding: 0 8px;
  resize: none;
  transition-duration: 0;
  width: 100%;
  min-height: 1.5em;
  overflow-y: hidden;

  &::placeholder {
    padding-top: 8px;
  }

  /* Disabled state styles */
  &[aria-disabled='true'] {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: sandstone.79;
    border-color: sandstone.79;
  }
`
/**
 * Displays a chat input interface.
 */
export const ChatInput = vui<'input', ChatInputProps>((props, ref) => {
  const {
    helpText,
    placeholder = 'Type your message',
    status = 'default',
    maxTextareaHeight = 120,
    processingMessage = 'Generating message',
    onNewTopicClick,
    onSend,
    onStop,
    ...rest
  } = props

  const [value, setValue] = useState<string>('')
  const styles = useStyleConfig('Chat', props)

  useEffect(() => {
    if (status === 'default') setValue('')
  }, [status])

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.stopPropagation()
      if (value?.length && status === 'default') onSend?.(value)
    }

    if (e.key === 'Escape') {
      e.stopPropagation()
      if (status === 'processing') onStop?.()
    }
  }

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target
    textarea.placeholder = ''

    // Dynamically adjust the height of the textarea

    if (textarea.scrollHeight <= maxTextareaHeight) {
      textarea.style.height = 'auto'
      textarea.style.maxHeight = 'auto'
      textarea.style.height = `${textarea.scrollHeight}px`
      textarea.style.maxHeight = `${textarea.scrollHeight}px`
      textarea.style.lineHeight = '2.2'
    }

    if (status === 'default') setValue(textarea.value)
  }

  const isDisabled = status === 'disabled'

  return (
    <Box className={cs('vui-chat-input')} justifyContent="space-between" ref={ref} {...styles?.input} {...rest}>
      {!!onNewTopicClick && status === 'default' && (
        <Button animation="fadeUp" icon="uiPlus" mr={1} onClick={() => onNewTopicClick?.()} />
      )}
      {status === 'processing' ? (
        <Box borderColor={inputColors.border} borderWidth={1} center justifyContent="flex-start" py={0.5} w="100%">
          <Icon mx={1} name="uiSpinnerThird" pathFill={['blue.40', 'blue.60']} size="lg" variant="spinning" />
          <Box>{processingMessage}</Box>
        </Box>
      ) : (
        <Box animation="fadeUp" border="0" column outline="none" w="100%">
          <Box
            borderColor={inputColors.border}
            borderWidth={1}
            center
            focusWithinRingColor={inputColors.focus}
            justifyContent="flex-start"
            position="relative"
            w="100%"
          >
            <CustomTextarea
              disabled={isDisabled}
              onChange={handleTextareaChange}
              onFocus={handleTextareaChange}
              onKeyDown={onKeyDown as any}
              placeholder={placeholder}
              value={value}
            />
            <ChatInputButton
              disabled={!value?.length}
              h="100%"
              onSend={() => {
                if (value?.length) onSend?.(value)
              }}
              onStop={onStop}
              status={status as ChatInputStatus}
            />
          </Box>
          {!!helpText && <HelpText>{helpText}</HelpText>}
        </Box>
      )}
    </Box>
  )
})

ChatInput.displayName = 'ChatInput'
export default ChatInput
